<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import { useFormValidation } from "../../../../composables/useFormValidation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { required } from "../../../../utils/validation";
import {
  Item,
  RecordStatus,
  Warehouse,
  WarehouseShelf,
} from "../../../../types/models";
import {
  documentActions,
  downloadPDFBlob,
  formatDate,
  isEmpty,
  printPDFBlob,
  sharePDFBlob,
} from "../../../../utils/CommonFunctions";
import MessageBox from "../../../../services/MessageBox";

import { tokenManager } from "../../../../api/utils/tokenManager";
import ItemStockGrid from "../../../../components/ui/Templates/ItemStockGrid.vue";
import DocumentHeader from "../../../../components/ui/Templates/DocumentHeader.vue";
import DocumentFooter from "../../../../components/ui/Templates/DocumentFooter.vue";
import ModalFormComponent from "../../../../components/ui/common/ModalFormComponent.vue";
import GridInput from "../../../../components/ui/form/GridInput.vue";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import DateInput from "../../../../components/ui/form/DateInput.vue";
import DataTable from "../../../../components/table/DataTable.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus" | string;
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
  recordStatus?: RecordStatus[];
  cartItems?: Item[];
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "save"): void;
  (e: "viewDocument", data: any, title: string, routePath: string): void;
}>();

const isReadOnly = ref<boolean>(props.mode === "view");

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  setFormData,
  resetForm,
} = useFormValidation({
  id: props.data?.id || "",
  stock_requests_id: props.data?.stock_requests_id || "",
  from_warehouses_id: props.data?.from_warehouses_id || "",
  to_warehouses_id: props.data?.to_warehouses_id || "",
  transit_warehouses_id: props.data?.transit_warehouses_id || "",
  transit_warehouse: props.data?.transit_warehouse || "",
  date_time: props.data?.date_time || new Date().toISOString().split("T")[0],
  currency: props.data?.currency || tokenManager.getSetting("default_currency", true) || "USD",
  exchange_rate: props.data?.exchange_rate || 1,
  currencys_id: props.data?.currencys_id || 1,
  prefix: props.data?.prefix || "",
  ref_no: props.data?.ref_no || "",
  attachment: props.data?.attachment || "",
  uid: props.data?.id || "",
  items: [] as any,
  receipts: props.data?.receipts || [] as any,
  status: props.data?.status || "Pending",
  document_no: props.data?.document_no || "",
  download: false,
  action: "",
});

const warehouse = ref<Warehouse>();

//setFieldRules("outlets_id", [required("Select Outlet")]);
setFieldRules("from_warehouses_id", [required("Select From Warehouse")]);
setFieldRules("to_warehouses_id", [required("Select To Warehouse")]);
//setFieldRules("transit_warehouses_id", [required("Select Transit Warhouse")]);
setFieldRules("items", [required("Select at least 1 item")]);

const handleFromWarehouseSelected = async (item: any) => {
  warehouse.value = item;
  prepareItemRow();
};

const getRecord = async () => {
  if (isEmpty(props.data)) return;
  const params = {
    id: props.data.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    params
  );
  if (response.success == true) {
    setFormData(response.data);
    fields.value.date_time.value = formatDate(response.data.date_time);
  }
};

const validateGrid = async (): Promise<boolean> => {
  for (const item of formData.value.items) {
    if (item.code && item.name && item.items_id && (item.inventory_qty ?? 0)  > 0) {
    if (item.tracked_by === "Batch") {
      const batch_qty = item.batches?.reduce(
        (sum: number, batch: { quantity: number }) => {
          if (typeof batch.quantity === "number") {
            return sum + batch.quantity;
          }
          return sum;
        },
        0
      );
      if (batch_qty !== item.inventory_qty) {
        return false;
      }
    } else if (item.tracked_by === "Serial") {
      const serial_qty = item.serials?.reduce(
        (sum: number, serial: { quantity: number }) => {
          if (typeof serial.quantity === "number") {
            return sum + serial.quantity;
          }
          return sum;
        },
        0
      );
      if (serial_qty !== item.inventory_qty) {
        return false;
      }
    }

    if (warehouse.value?.shelf_managed) {
      const shelf_qty = item.shelfs?.reduce(
        (sum: number, shelf: WarehouseShelf) => {
          if (
            typeof shelf.quantity === "number"
          ) {
            return sum + shelf.quantity;
          }
          return sum;
        },
        0
      );
      if (shelf_qty !== item.inventory_qty) {
        return false;
      }
    }
  }
  }
  
  return true;
};

const handleSubmit = async (
  new_record: boolean,
  download: boolean = false,
  mail: boolean = false,
  share: boolean = false,
  print = false
) => {
  if (validateForm()) {
    const isValid = await validateGrid();
    if (!isValid) {
      MessageBox.show(
        "There are some Batch/Serial/Shelf Quantity differences",
        "warning"
      );
      return;
    }
    let items = formData.value.items.filter((e: any) => e.items_id != "");
    if (items.length <= 0) {
      fields.value.items.error = "Select at least 1 item";
      //MessageBox.show("Select at least 1 item", "warning");
      return;
    }
    if(formData.value.from_warehouses_id == formData.value.to_warehouses_id){
      fields.value.transit_warehouses_id.error = "From Warehouse and To Warehouse should not be same";
      return;
    }
    formData.value.download = download || share || print;
    formData.value.action =
      download || share || print ? "download" : mail ? "mail" : "";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + "create",
      formData.value
    );
    if (response.success == true) {
      isReadOnly.value = true;
      Toast.show("saved successfully");
      emit("save");
      if (new_record) {
        clearForm();
      } else if (download) {
        downloadPDFBlob(response.data.file, 'Stock Transfer #: ' + response.data.data.document_no);
      } else if (mail) {
        if (!response.data.error) {
          Toast.show('Stock Transfer #: ' + response.data.data.document_no + " emailed successfully");
        }
      } else if (share) {
        sharePDFBlob(
          response.data.file, 'Stock Transfer #: ' + response.data.data.document_no
        );
      } else if (print) {
        printPDFBlob(response.data.file);
      }
      setFormData(response.data.data);
    }
  }
};

const clearForm = () => {
  resetForm();

  fields.value.date_time.value = formatDate(new Date());
  fields.value.to_warehouses_id.value = tokenManager.getSetting(
    "default_warehouse",
    true
  );
  isReadOnly.value = false;

  nextTick(() => {
    const input = document.getElementById("customers_id");
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
};

const allowNewItemRow = ref(false);

const prepareItemRow = () => {
  if (
    !isEmpty(formData.value.from_warehouses_id) &&
    !isEmpty(formData.value.to_warehouses_id)
  ) {
    if (props.cartItems) prepareCartItems();
    allowNewItemRow.value = true;
  } else {
    allowNewItemRow.value = false;
  }
};

const prepareCartItems = async () => {
  const ids = props.cartItems?.map((item) => item.id);
  const response = await api.post(
    "clients/inventory/item-management/items/items",
    {
      ids: ids,
      warehouses_id: formData.value.from_warehouses_id,
    }
  );
  if (response.success == true) {
    if (itemGridRef.value) {
      itemGridRef.value.handleSelectedItems(response.data);
    }
  }
};

const itemGridRef = ref<InstanceType<typeof ItemStockGrid> | null>(null);

const updateItems = (items: Item[] | []) => {
  formData.value.items = items;
};

const handleActions = async (action: string) => {
  documentActions(props.endPoint, formData.value.uid, action, "Stock Transfer# " + formData.value.ref_no);
};

const formMode = ref('')

const showForm = ref(false);
onMounted(() => {
  showForm.value = true;
  formMode.value = props.mode
  isReadOnly.value = props.mode == "view";
  if (props.mode != "create") {
    getRecord();
  } else {
    clearForm();
  }
});
</script>

<template>
  <ModalFormComponent width="100%" @close="$emit('close')">
    <template #header>
      <!-- Header -->
      <DocumentHeader
        :title="title"
        :end-point="endPoint"
        :data="formData"
        :readonly="isReadOnly"
        :show-drop-down="isReadOnly"
        @close="$emit('close')"
        :permissions="permissions"
        @clear="clearForm"
        @new="clearForm"
        @save="handleSubmit(false)"
        @download="handleSubmit(false, true)"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="formData.items.length > 0"
      >
        <template #dropdownbuttons> </template>
      </DocumentHeader>
    </template>
    <!-- Main Content - Scrollable -->
    <template #form>
      <!--header-->
      <div class="grid md:grid-cols-5 grid-cols-2 gap-6 mb-8">
        <SearchSelect
          label="From Warehouse"
          end-point="system/company/warehouses"
          v-model="fields.from_warehouses_id.value"
          :readonly="isReadOnly"
          @select="handleFromWarehouseSelected"
          :error="
            fields.from_warehouses_id.touched ? fields.from_warehouses_id.error : null
          "
          @blur="fields.from_warehouses_id.touched = true"
        />
        <SearchSelect
          label="To Warehouse"
          end-point="system/company/warehouses"
          v-model="fields.to_warehouses_id.value"
          :readonly="isReadOnly"
          @select="prepareItemRow"
          :error="
            fields.to_warehouses_id.touched ? fields.to_warehouses_id.error : null
          "
          @blur="fields.to_warehouses_id.touched = true"
        />
        <div class="col-span-2 grid grid-cols-3 gap-6">
          <TextInput
            v-model="fields.transit_warehouse.value"
            label="Transit Warehouse"
            v-if="mode != 'create'"
            :readonly="true"
            :error="fields.transit_warehouse.touched ? fields.transit_warehouse.error : null"
            @blur="fields.transit_warehouse.touched = true" />
          <DateInput
            v-model="fields.date_time.value"
            label="Date"
            :readonly="isReadOnly"
            :error="fields.date_time.touched ? fields.date_time.error : null"
            @blur="fields.date_time.touched = true" />
          <TextInput
            v-model="fields.document_no.value"
            v-if="mode != 'create'"
            label="Document No"
            :readonly="true"
            :error="fields.document_no.touched ? fields.document_no.error : null"
            @blur="fields.document_no.touched = true" />

        </div>
      </div>
      <GridInput label="Items" :error="fields.items.touched ? fields.items.error : null">
        <ItemStockGrid
        ref="itemGridRef"
        :items="formData.items"
        :readonly="isReadOnly"
        :currency="formData.currency"
        :exchange_rate="formData.exchange_rate"
        :warehouses_id="formData.from_warehouses_id"
        :allow-change-price="false"
        :allow-discount="true"
        :allow-new-row="allowNewItemRow"
        :shelf-managed="warehouse?.shelf_managed ?? false"
        :shelfs="warehouse?.shelfs"
        @items="updateItems"
      />
      </GridInput>
      <GridInput label="Stock Receipts" v-if="formData.receipts && formData.receipts.length > 0">
        <DataTable
          title="Stock Receipts"
          :rows="formData.receipts"
          :fields="[
            { name: 'date_time', caption: 'Date', sortable: false, fieldType: 'date' },
            { name: 'ref_no', caption: 'Ref. No', sortable: false, fieldType: 'text' },
            { name: 'user', caption: 'Received User', sortable: false, fieldType: 'text' },
            { name: 'status', caption: 'Status', sortable: false, fieldType: 'text' },
          ]"
          :show-header="true"
          :show-footer="false"
          :show-pagination="false"
          :show-search="false"
          :show-filter="false"
          :show-select="false"
          :show-actions="false"
          child-row-data-field="items"
          child-row-table-width="500px"
          :child-row-data="formData.receipts.items"
          :show-child-row="true"
          :child-row-fields="[
            { name: 'code', caption: 'Code', sortable: false, fieldType: 'text' },
            { name: 'name', caption: 'Item Name', sortable: false, fieldType: 'text' },
            { name: 'transfer_qty', caption: 'Transfer Qty', sortable: false, fieldType: 'number' },
            { name: 'remaining_qty', caption: 'Remaining Qty', sortable: false, fieldType: 'number' },
          ]" />
      </GridInput>
    </template>
    <!-- Footer -->
    <template #footer>
      <DocumentFooter
        :data="formData"
        :readonly="isReadOnly"
        :end-point="endPoint"
        :permissions="permissions"
        :title="title"
        convert-module="Debit Note"
        @close="$emit('close')"
        @save="handleSubmit(false)"
        @save-new="handleSubmit(true)"
        @download="handleSubmit(false, true)"
        @new="clearForm"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="formData.items.length > 0"
      />
    </template>
  </ModalFormComponent>
</template>
